import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Edit3 } from 'lucide-react';
import { useTaskComments } from '@/hooks/useTaskComments';
import { useGrowthSquad } from '@/hooks/useGrowthSquad';
import { MentionTextarea } from '@/components/ui/mention-textarea';

interface EditCommentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskType: 'foundational' | 'growth' | 'recurring';
  commentId: string;
  initialText: string;
  onSuccess?: () => void;
}

export const EditCommentDialog: React.FC<EditCommentDialogProps> = ({
  isOpen,
  onClose,
  taskId,
  taskType,
  commentId,
  initialText,
  onSuccess,
}) => {
  const [commentText, setCommentText] = useState(initialText);
  const [mentions, setMentions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateComment } = useTaskComments(taskId, taskType);
  const { squadMembers } = useGrowthSquad();

  const mentionOptions = squadMembers.map(member => ({
    id: member.id,
    name: member.name,
    alias: member.alias,
  }));

  const handleSubmit = async () => {
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    try {
      await updateComment(commentId, commentText.trim());
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCommentText(initialText);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName="Edit Comment" 
            popupContext="/my-tasks"
            isOpen={isOpen}
          >
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4" />
              Edit Comment
            </div>
          </DialogTitleWithBug>
        </DialogHeader>

        <div className="space-y-4">
          <MentionTextarea
            value={commentText}
            onChange={setCommentText}
            onMentionsChange={setMentions}
            mentionOptions={mentionOptions}
            placeholder="Edit your comment... Use @ to mention team members"
            className="min-h-[120px]"
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || !commentText.trim()}
            >
              {isSubmitting ? 'Updating...' : 'Update Comment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};